import { useEffect, useRef, useState } from "react";
import api, { fmtTRY } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import MediaUploader from "../components/common/MediaUploader";
import { Plus, Pencil, Trash2, FileUp, Download, Search, AlertTriangle, FileSpreadsheet, Printer, QrCode } from "lucide-react";
import { downloadFile } from "../lib/download";
import { QRCodeSVG } from "qrcode.react";

const empty = { oem_no: "", name: "", category: "", quantity: 0, min_quantity: 1, purchase_price: 0, sale_price: 0, shelf_location: "", description: "", media: [] };

export default function PartsPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [showLow, setShowLow] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [labelsOpen, setLabelsOpen] = useState(false);
  const [labelSize, setLabelSize] = useState("medium"); // small/medium/large
  const fileRef = useRef();

  const load = () => api.get(`/parts${showLow ? "?low_stock=true" : ""}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [showLow]);

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Parça adı zorunlu");
    const p = { ...form, quantity: Number(form.quantity) || 0, min_quantity: Number(form.min_quantity) || 0, purchase_price: Number(form.purchase_price) || 0, sale_price: Number(form.sale_price) || 0 };
    try {
      if (editing) await api.put(`/parts/${editing}`, p);
      else await api.post("/parts", p);
      toast.success("Kaydedildi"); setOpen(false); setForm(empty); setEditing(null); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const edit = (p) => { setEditing(p.id); setForm({ ...empty, ...p, media: p.media || [] }); setOpen(true); };
  const remove = async (id) => { if (window.confirm("Silinsin mi?")) { await api.delete(`/parts/${id}`); toast.success("Silindi"); load(); } };

  const downloadTemplate = () => {
    const rows = [
      ["OEM No", "Parça Adı", "Kategori", "Adet", "Min Adet", "Alış Fiyatı", "Satış Fiyatı", "Raf Konumu"],
      ["MF-123-45", "Yağ Filtresi", "Filtre", 25, 5, 120, 180, "A-01"],
      ["JD-98-11", "Motor Yağı 15W40", "Yağ", 40, 10, 450, 620, "B-02"],
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replaceAll('"', '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "yedek_parca_sablon.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const runImport = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Dosya seçin");
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post("/parts/import-excel", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportResult(data);
      toast.success(`${data.created} kayıt aktarıldı`);
      load();
    } catch (e) { toast.error("Aktarım başarısız: " + (e?.response?.data?.detail || "")); }
  };

  const filtered = items.filter((p) => (p.name + (p.oem_no || "") + (p.category || "")).toLowerCase().includes(q.toLowerCase()));
  const selectedParts = items.filter((p) => selected.has(p.id));
  const toggleSel = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const toggleAll = () => { if (selected.size === filtered.length) setSelected(new Set()); else setSelected(new Set(filtered.map((p) => p.id))); };
  const openLabels = () => { if (selected.size === 0) return toast.error("Etiket için parça seçin"); setLabelsOpen(true); };

  return (
    <div className="space-y-5" data-testid="parts-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Depo</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">Yedek Parça</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={openLabels} className="gap-2" data-testid="print-labels-btn"><QrCode size={16} /> Etiket Yazdır {selected.size > 0 ? `(${selected.size})` : ""}</Button>
          <Button variant="outline" onClick={() => downloadFile("/exports/parts-stock.xlsx", "yedek-parca-stok.xlsx")} className="gap-2" data-testid="export-parts-btn"><FileSpreadsheet size={16} /> Excel İndir</Button>
          <Button variant="outline" onClick={() => setImportOpen(true)} className="gap-2" data-testid="import-excel-btn"><FileUp size={16} /> Excel/CSV Aktar</Button>
          <Button className="bg-[#1B4D3E] hover:bg-[#163e32] text-white gap-2" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-part-btn">
            <Plus size={16} /> Yeni Parça
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="OEM no, ad veya kategori ile ara…" className="pl-9" data-testid="parts-search" />
        </div>
        <Button variant={showLow ? "default" : "outline"} className={showLow ? "bg-rose-600 hover:bg-rose-700 gap-2" : "gap-2"} onClick={() => setShowLow(!showLow)} data-testid="low-stock-toggle">
          <AlertTriangle size={16} /> Kritik Stok
        </Button>
      </div>

      <Card className="border-slate-200">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"><input type="checkbox" checked={selected.size > 0 && selected.size === filtered.length} onChange={toggleAll} data-testid="part-select-all" /></TableHead>
                <TableHead>OEM No</TableHead>
                <TableHead>Parça</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead className="text-right">Adet</TableHead>
                <TableHead className="text-right">Satış</TableHead>
                <TableHead>Raf</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((p) => {
                const low = Number(p.quantity) <= Number(p.min_quantity);
                return (
                  <TableRow key={p.id} data-testid={`part-row-${p.id}`}>
                    <TableCell><input type="checkbox" checked={selected.has(p.id)} onChange={() => toggleSel(p.id)} data-testid={`part-select-${p.id}`} /></TableCell>
                    <TableCell className="font-mono text-xs">{p.oem_no || "—"}</TableCell>
                    <TableCell className="font-semibold text-slate-900">{p.name}</TableCell>
                    <TableCell><Badge variant="outline">{p.category || "—"}</Badge></TableCell>
                    <TableCell className="text-right">
                      <span className={low ? "text-rose-600 font-bold" : "font-bold"}>{p.quantity}</span>
                    </TableCell>
                    <TableCell className="text-right font-bold text-[#1B4D3E]">{fmtTRY(p.sale_price)}</TableCell>
                    <TableCell className="text-xs">{p.shelf_location || "—"}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => edit(p)} data-testid={`edit-part-${p.id}`}><Pencil size={14} /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(p.id)} data-testid={`delete-part-${p.id}`}><Trash2 size={14} className="text-rose-600" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filtered.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Kayıt bulunamadı</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Parça Düzenle" : "Yeni Parça"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>OEM/Parça No</Label><Input value={form.oem_no} onChange={(e) => setForm({ ...form, oem_no: e.target.value })} data-testid="pt-oem" /></div>
            <div><Label>Parça Adı *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pt-name" /></div>
            <div><Label>Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="pt-cat" /></div>
            <div><Label>Raf Konumu</Label><Input value={form.shelf_location} onChange={(e) => setForm({ ...form, shelf_location: e.target.value })} data-testid="pt-shelf" /></div>
            <div><Label>Adet</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="pt-qty" /></div>
            <div><Label>Min Adet</Label><Input type="number" value={form.min_quantity} onChange={(e) => setForm({ ...form, min_quantity: e.target.value })} data-testid="pt-min" /></div>
            <div><Label>Alış Fiyatı (₺)</Label><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="pt-purchase" /></div>
            <div><Label>Satış Fiyatı (₺)</Label><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="pt-sale" /></div>
            <div className="sm:col-span-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="pt-desc" /></div>
            <div className="sm:col-span-2"><Label>Fotoğraf / Video</Label><MediaUploader value={form.media} onChange={(m) => setForm({ ...form, media: m })} testid="pt-media" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={submit} data-testid="save-part-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Excel import */}
      <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportResult(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Excel/CSV'den Stok Aktarımı</DialogTitle>
            <DialogDescription>Sütunlar: OEM No, Parça Adı, Kategori, Adet, Min Adet, Alış Fiyatı, Satış Fiyatı, Raf Konumu</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button variant="outline" onClick={downloadTemplate} className="w-full gap-2" data-testid="download-template-btn"><Download size={16} /> Örnek Şablon İndir (CSV)</Button>
            <div className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-6 text-center">
              <FileUp className="mx-auto text-slate-400" size={32} />
              <div className="mt-2 text-sm text-slate-600">Excel (.xlsx) veya CSV dosyası seçin</div>
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="mt-3 mx-auto block text-sm" data-testid="import-file-input" />
            </div>
            {importResult && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-lg p-3 text-sm" data-testid="import-result">
                <div><span className="font-bold">{importResult.created}</span> / {importResult.total_rows} kayıt aktarıldı.</div>
                {importResult.errors?.length > 0 && (
                  <details className="mt-2"><summary className="cursor-pointer text-xs">Hatalar ({importResult.errors.length})</summary>
                    <ul className="mt-1 space-y-1 text-xs">
                      {importResult.errors.slice(0, 20).map((e, i) => <li key={i}>Satır {e.row}: {e.error}</li>)}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Kapat</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={runImport} data-testid="run-import-btn">Aktar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Labels Print */}
      <Dialog open={labelsOpen} onOpenChange={setLabelsOpen}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between pr-6">
              <span>Yedek Parça Etiket Yazdırma ({selectedParts.length} adet)</span>
              <div className="flex items-center gap-2">
                <Select value={labelSize} onValueChange={setLabelSize}>
                  <SelectTrigger className="h-9 w-32" data-testid="label-size"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="small">Küçük (6 sütun)</SelectItem>
                    <SelectItem value="medium">Orta (4 sütun)</SelectItem>
                    <SelectItem value="large">Büyük (3 sütun)</SelectItem>
                  </SelectContent>
                </Select>
                <Button size="sm" variant="outline" className="gap-1 no-print" onClick={() => window.print()} data-testid="do-print-labels-btn">
                  <Printer size={14} /> Yazdır / PDF
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className={`print-page grid gap-3 ${labelSize === "small" ? "grid-cols-6" : labelSize === "large" ? "grid-cols-3" : "grid-cols-4"}`}>
            {selectedParts.map((p) => (
              <div key={p.id} className="border border-slate-300 rounded-lg p-2 flex flex-col items-center text-center bg-white break-inside-avoid" style={{ pageBreakInside: "avoid" }}>
                <QRCodeSVG value={JSON.stringify({ id: p.id, oem: p.oem_no || "", name: p.name })} size={labelSize === "small" ? 60 : labelSize === "large" ? 100 : 80} includeMargin={false} level="M" />
                <div className="mt-1.5 text-[10px] font-mono font-bold text-slate-700 truncate w-full">{p.oem_no || "—"}</div>
                <div className={`font-bold text-slate-900 leading-tight ${labelSize === "small" ? "text-[10px]" : "text-xs"} line-clamp-2`}>{p.name}</div>
                {p.shelf_location && <div className="text-[9px] text-slate-500 mt-0.5">Raf: {p.shelf_location}</div>}
                <div className={`font-extrabold text-[#1B4D3E] mt-1 ${labelSize === "small" ? "text-xs" : "text-sm"}`}>{fmtTRY(p.sale_price)}</div>
              </div>
            ))}
          </div>
          {selectedParts.length === 0 && <div className="text-center py-10 text-slate-500">Etiket için parça seçin.</div>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
